import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React from 'react';

import { useCustomStore } from '../../../../hooks';
import type { MotorClubMembershipDetailsResponse } from '../../../../store/api/motorClubMembership.api';

interface ViewMembershipDetailsModalProps {
  onClose: () => void;
  open: boolean;
  showDriverView?: boolean;
}

const ViewMembershipDetailsModal: React.FC<ViewMembershipDetailsModalProps> = ({
  onClose,
  open,
  showDriverView,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { motorClubMembershipStore } = useCustomStore();
  const [details, setDetails] =
    React.useState<MotorClubMembershipDetailsResponse | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setDetails(null);
      return;
    }

    const loadMembershipDetails = async () => {
      setIsLoadingDetails(true);
      const responseOrErr = showDriverView
        ? await motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore.getDriverMembershipDetails()
        : await motorClubMembershipStore.getMembershipDetails();

      if (responseOrErr?.isOk?.()) {
        setDetails(responseOrErr.value);
      }

      if (responseOrErr?.isErr?.()) {
        setDetails(null);
        enqueueSnackbar(responseOrErr.error.message, {
          variant: 'error',
        });
      }

      setIsLoadingDetails(false);
    };

    loadMembershipDetails();
  }, [enqueueSnackbar, showDriverView, motorClubMembershipStore, open]);

  const status = details?.status || 'Unknown';
  const isActive = status.toLowerCase().indexOf('active') >= 0;
  const statusColor = isActive ? '#239451' : '#66666F';

  const formatDate = (value?: string) => {
    if (!value) {
      return '--';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getMembershipDurationDays = (
    startValue?: string,
    endValue?: string,
  ) => {
    if (!startValue || !endValue) {
      return '--';
    }

    const start = new Date(startValue);
    const end = new Date(endValue);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return '--';
    }

    const startOfTerm = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );
    const endOfTerm = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
    );

    const diffMs = endOfTerm.getTime() - startOfTerm.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1);
  };

  const currentTerm =
    details?.currentTermStart && details?.currentTermEnd
      ? `${formatDate(details.currentTermStart)} - ${formatDate(
          details.currentTermEnd,
        )}`
      : '--';

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          borderRadius: '14px',
          maxWidth: 'calc(100% - 32px)',
          overflow: 'hidden',
          width: '486px',
        },
      }}>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '16px 22px',
        }}>
        <Typography
          sx={{
            color: '#2B2B2B',
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '16px',
            lineHeight: 1,
            marginRight: '8px',
          }}>
          {'🪪'}
        </Typography>

        <Typography
          sx={{
            color: '#2B2B2B',
            flex: 1,
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '16px',
            lineHeight: '24px',
          }}>
          Subscription Details
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{
            color: '#66666F',
            marginRight: '-6px',
            marginTop: '-2px',
            padding: '4px',
          }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent
        sx={{ backgroundColor: '#FFFFFF', padding: '20px 22px 18px 22px' }}>
        {isLoadingDetails && !details ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              minHeight: '180px',
            }}>
            <CircularProgress size={26} sx={{ color: '#DEC330' }} />
          </Box>
        ) : null}

        {!isLoadingDetails ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Box sx={statusCardSx}>
              <Box>
                <Typography sx={labelSx}>Status</Typography>
                <Typography
                  sx={{
                    alignItems: 'center',
                    color: statusColor,
                    display: 'inline-flex',
                    fontFamily: 'FiraSans-SemiBold',
                    fontSize: '15px',
                    gap: '6px',
                    lineHeight: '22px',
                  }}>
                  <span>{'●'}</span>
                  {status}
                </Typography>
              </Box>

              <Box sx={{ marginLeft: 'auto', textAlign: 'right' }}>
                <Typography sx={labelSx}>Member Since</Typography>
                <Typography sx={valueStrongSx}>
                  {formatDate(details?.currentTermStart)}
                </Typography>
              </Box>
            </Box>

            <Box sx={contentCardSx}>
              <Typography sx={labelSx}>
                {showDriverView ? 'Member ID' : 'Subscription ID'}
              </Typography>
              <Typography sx={valueStrongSx}>
                {(showDriverView
                  ? details?.memberId
                  : details?.subscriptionId) || '--'}
              </Typography>
            </Box>

            {showDriverView ? null : (
              <Box sx={contentCardSx}>
                <Typography sx={labelSx}>Subscription Summary</Typography>
                <Typography sx={summaryLineSx}>
                  Active drivers {details?.activeMembers ?? '--'}
                </Typography>
                <Typography sx={summaryLineSx}>
                  Vacant drivers {details?.vacantMembers ?? '--'}
                </Typography>
                <Typography sx={summaryLineSx}>
                  Active units {details?.activeUnits ?? '--'}
                </Typography>
                <Typography sx={summaryLineSx}>
                  Vacant units {details?.vacantUnits ?? '--'}
                </Typography>
              </Box>
            )}

            <Box sx={contentCardSx}>
              <Typography sx={labelSx}>Current Term</Typography>
              <Typography sx={{ ...valueStrongSx, marginBottom: '2px' }}>
                {currentTerm}
              </Typography>
              <Typography sx={subValueSx}>
                {details?.programType || '--'}
              </Typography>
            </Box>

            <Box sx={renewalCardSx}>
              <Typography
                sx={{
                  color: '#8A1F3D',
                  fontFamily: 'FiraSans-SemiBold',
                  fontSize: '30px',
                  lineHeight: '30px',
                  marginRight: '11px',
                }}>
                {getMembershipDurationDays(
                  details?.currentTermStart,
                  details?.currentTermEnd,
                )}
              </Typography>
              <Typography sx={subValueSx}>days until renewal - </Typography>
              <Typography
                sx={{
                  ...subValueSx,
                  color: '#2B2B2B',
                  fontFamily: 'FiraSans-SemiBold',
                }}>
                {details?.currentTermEnd
                  ? `auto-renews ${formatDate(details.currentTermEnd)}`
                  : 'auto-renews --'}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <Divider />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '18px 22px 16px 22px',
        }}>
        <Button
          onClick={onClose}
          sx={{
            '&:hover': {
              backgroundColor: '#B69300',
            },
            backgroundColor: '#B69300',
            borderRadius: '8px',
            color: '#1A1A1A',
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '12.5px',
            minWidth: '88px',
            padding: '8px 16px',
            textTransform: 'none',
          }}>
          Ok
        </Button>
      </Box>
    </Dialog>
  );
};

const labelSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '11px',
  letterSpacing: '.4px',
  marginBottom: '4px',
  textTransform: 'uppercase',
};

const valueStrongSx = {
  color: '#2B2B2B',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '15px',
  lineHeight: '21px',
};

const subValueSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Regular',
  fontSize: '13px',
  lineHeight: '19px',
};

const summaryLineSx = {
  color: '#2B2B2B',
  fontFamily: 'FiraSans-Regular',
  fontSize: '13px',
  lineHeight: '20px',
};

const statusCardSx = {
  alignItems: 'center',
  backgroundColor: '#E5F2EC',
  borderRadius: '10px',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '16px',
};

const contentCardSx = {
  border: '1px solid #D8D8DE',
  borderRadius: '10px',
  padding: '14px 16px',
};

const renewalCardSx = {
  alignItems: 'center',
  backgroundColor: '#F7F7F8',
  border: '1px solid #D8D8DE',
  borderRadius: '10px',
  display: 'flex',
  flexWrap: 'wrap',
  padding: '14px 16px',
};

export default ViewMembershipDetailsModal;
